import React, { useState, useEffect, useCallback } from 'react';
import PeriodDetailModal from './PeriodDetailModal.tsx';
import { AppIcon, AppIconName } from '../../icons/appIcons.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface FinancialData {
  period: string;
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalOverdue: number;
  invoiceCount: number;
  paidInvoiceCount: number;
  averageInvoiceAmount: number;
  averagePaymentTime: number;
  membershipRevenue: number;
  lessonRevenue: number;
  eventRevenue: number;
  equipmentRevenue: number;
  donationRevenue: number;
  otherRevenue: number;
}

interface FinancialSummary {
  monthly: FinancialData[];
  quarterly: FinancialData[];
  annual: FinancialData[];
  currentStats: {
    totalRevenue: number;
    totalOutstanding: number;
    collectionRate: number;
    averageInvoiceValue: number;
  };
}

export default function FinancialReports() {
  const [data, setData] = useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [revenueBreakdownFilter, setRevenueBreakdownFilter] = useState<string>('ytd');
  const [selectedPeriodDetail, setSelectedPeriodDetail] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchFinancialData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      console.log('🔍 Fetching financial data for year:', selectedYear);
      console.log('🔑 Using token:', token ? 'Token present' : 'No token');
      
      const response = await fetch(`/api/admin/financial-reports?year=${selectedYear}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📊 Response status:', response.status);
      const result = await response.json();
      console.log('📊 Response data:', result);
      
      if (result.success) {
        setData(result.data);
        console.log('✅ Financial data loaded successfully');
      } else {
        console.error('❌ API returned error:', result.message);
      }
    } catch (error) {
      console.error('❌ Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchFinancialData();
  }, [fetchFinancialData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getCurrentPeriodData = () => {
    if (!data) return [];
    return data[selectedPeriod] || [];
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  const calculateTrend = (currentValue: number, previousValue: number) => {
    if (previousValue === 0) return { trend: 'neutral', percentage: 0 };
    const change = ((currentValue - previousValue) / previousValue) * 100;
    return {
      trend: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
      percentage: Math.abs(change)
    };
  };

  const getTrendLabel = (trend: string) => {
    switch (trend) {
      case 'up': return '↑';
      case 'down': return '↓';
      default: return '→';
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getRevenueBreakdownData = () => {
    if (!data) return [];
    
    if (revenueBreakdownFilter === 'ytd') {
      // Return all data for YTD calculation
      return data.monthly || [];
    } else {
      // Find specific period
      const allPeriods = [...(data.monthly || []), ...(data.quarterly || [])];
      const filtered = allPeriods.filter(p => p.period === revenueBreakdownFilter);
      return filtered.length > 0 ? filtered : [];
    }
  };

  const getRevenueFilterOptions = () => {
    if (!data) return [];
    
    const options = [{ value: 'ytd', label: `Year to Date ${selectedYear}` }];
    
    // Add monthly options
    if (data.monthly) {
      data.monthly.forEach(m => {
        options.push({ value: m.period, label: m.period });
      });
    }
    
    // Add quarterly options
    if (data.quarterly) {
      data.quarterly.forEach(q => {
        options.push({ value: q.period, label: q.period });
      });
    }
    
    return options;
  };

  const handlePeriodClick = (periodName: string) => {
    setSelectedPeriodDetail(periodName);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="px-12 py-10">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ggaa-green"></div>
          <span className="ml-2 text-gray-600">Loading financial reports...</span>
        </div>
      </div>
    );
  }

  const periodData = getCurrentPeriodData();
  const currentStats = data?.currentStats;

  console.log('🔍 Financial Reports Render:', {
    loading,
    hasData: !!data,
    currentStats,
    periodDataLength: periodData.length,
    selectedPeriod,
    selectedYear
  });

  if (!loading && !data) {
    return (
      <div className="px-12 py-10">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <EmptyState
              icon="emptyChart"
              title="No Financial Data Available"
              description="Unable to load financial reports. Check console for errors."
            />
            <button 
              onClick={fetchFinancialData}
              className="bg-ggaa-green text-white px-4 py-2 rounded-lg hover:bg-ggaa-green/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-12 py-10">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financial Reports</h1>
          <p className="text-gray-600 mt-2">Track revenue, payments, and financial performance</p>
        </div>
        <div className="flex space-x-4">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="border border-gray-300 rounded-lg pl-3 pr-10 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
          >
            {getYearOptions().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Overall Statistics */}
      {currentStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-ggaa-green/20">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Total Revenue</h3>
            <div className="text-3xl font-bold text-ggaa-green">{formatCurrency(currentStats.totalRevenue)}</div>
            <p className="text-xs text-gray-500 mt-1">Year to date</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Outstanding Amount</h3>
            <div className="text-3xl font-bold text-yellow-600">{formatCurrency(currentStats.totalOutstanding)}</div>
            <p className="text-xs text-gray-500 mt-1">Unpaid invoices</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Collection Rate</h3>
            <div className="text-3xl font-bold text-blue-600">{formatPercentage(currentStats.collectionRate)}</div>
            <p className="text-xs text-gray-500 mt-1">Payments received</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Avg Invoice Value</h3>
            <div className="text-3xl font-bold text-purple-600">{formatCurrency(currentStats.averageInvoiceValue)}</div>
            <p className="text-xs text-gray-500 mt-1">Per invoice</p>
          </div>
        </div>
      )}

      {/* Period Selection */}
      <div className="mb-6 flex space-x-2">
        {(['monthly', 'quarterly', 'annual'] as const).map(period => (
          <button
            key={period}
            onClick={() => setSelectedPeriod(period)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedPeriod === period 
                ? 'bg-ggaa-green text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {period.charAt(0).toUpperCase() + period.slice(1)} View
          </button>
        ))}
      </div>

      {/* Financial Data Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)} Financial Performance
            </h3>
            <p className="text-sm text-gray-500 mt-1">Click on any row to view detailed invoices and donations</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Invoiced</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Paid</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outstanding</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collection Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {periodData.map((period, index) => {
                const collectionRate = period.totalInvoiced > 0 ? (period.totalPaid / period.totalInvoiced) * 100 : 0;
                const previousPeriod = periodData[index + 1];
                const paidTrend = previousPeriod ? calculateTrend(period.totalPaid, previousPeriod.totalPaid) : { trend: 'neutral', percentage: 0 };
                
                return (
                  <tr 
                    key={period.period} 
                    onClick={() => handlePeriodClick(period.period)}
                    className="hover:bg-ggaa-light cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{period.period}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatCurrency(period.totalInvoiced)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm text-gray-900">{formatCurrency(period.totalPaid)}</div>
                        {previousPeriod && (
                          <span className={`ml-2 text-xs ${getTrendColor(paidTrend.trend)}`}>
                            {getTrendLabel(paidTrend.trend)} {formatPercentage(paidTrend.percentage)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatCurrency(period.totalOutstanding)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${collectionRate >= 80 ? 'text-green-600' : collectionRate >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {formatPercentage(collectionRate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{period.invoiceCount}</div>
                      <div className="text-xs text-gray-500">{period.paidInvoiceCount} paid</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatCurrency(period.averageInvoiceAmount)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Revenue by Type */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Breakdown</h3>
            <select
              value={revenueBreakdownFilter}
              onChange={(e) => setRevenueBreakdownFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg pl-3 pr-10 py-1.5 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
            >
              {getRevenueFilterOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {(() => {
            const breakdownData = getRevenueBreakdownData();
            if (breakdownData.length === 0) {
              return <p className="text-gray-500 text-sm">No data available for selected period</p>;
            }
            
            return (
              <div className="space-y-4">
                {([
                  { label: 'Membership', value: breakdownData.reduce((sum, p) => sum + p.membershipRevenue, 0), icon: 'membership' as AppIconName, color: 'bg-ggaa-green' },
                  { label: 'Lessons', value: breakdownData.reduce((sum, p) => sum + p.lessonRevenue, 0), icon: 'lesson' as AppIconName, color: 'bg-blue-500' },
                  { label: 'Events', value: breakdownData.reduce((sum, p) => sum + p.eventRevenue, 0), icon: 'event' as AppIconName, color: 'bg-purple-500' },
                  { label: 'Equipment', value: breakdownData.reduce((sum, p) => sum + p.equipmentRevenue, 0), icon: 'equipment' as AppIconName, color: 'bg-yellow-500' },
                  { label: 'Donations', value: breakdownData.reduce((sum, p) => sum + (p.donationRevenue || 0), 0), icon: 'donation' as AppIconName, color: 'bg-pink-500' },
                  { label: 'Other', value: breakdownData.reduce((sum, p) => sum + p.otherRevenue, 0), icon: 'other' as AppIconName, color: 'bg-gray-500' }
                ] as const).map(item => {
                  const totalRevenue = breakdownData.reduce((sum, p) => sum + p.totalPaid, 0);
                  const percentage = totalRevenue > 0 ? (item.value / totalRevenue) * 100 : 0;
                  
                  return (
                    <div key={item.label} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${item.color} mr-3`}></div>
                        <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-2">
                          <AppIcon name={item.icon} className="w-4 h-4" />
                          {item.label}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(item.value)}</div>
                        <div className="text-xs text-gray-500">{formatPercentage(percentage)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Key Metrics */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Performance Indicators</h3>
          <div className="space-y-4">
            {periodData.length > 0 && (
              <>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-green-800">Average Payment Time</p>
                    <p className="text-xs text-green-600">Days from invoice to payment</p>
                  </div>
                  <span className="text-lg font-bold text-green-700">
                    {(periodData.reduce((sum, p) => sum + p.averagePaymentTime, 0) / periodData.length).toFixed(1)} days
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Total Invoices Issued</p>
                    <p className="text-xs text-blue-600">For {selectedYear}</p>
                  </div>
                  <span className="text-lg font-bold text-blue-700">
                    {periodData.reduce((sum, p) => sum + p.invoiceCount, 0)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-purple-800">Overdue Amount</p>
                    <p className="text-xs text-purple-600">Total overdue invoices</p>
                  </div>
                  <span className="text-lg font-bold text-purple-700">
                    {formatCurrency(periodData.reduce((sum, p) => sum + p.totalOverdue, 0))}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Period Detail Modal */}
      <PeriodDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        period={selectedPeriodDetail || ''}
        year={selectedYear}
      />
    </div>
  );
} 